/**
 * Albo fornitori per categoria merceologica.
 *
 * La N:N `fornitore ↔ tipologia di fornitura` esiste da sprint 02 ed è **vuota**. Riempirla è
 * censimento umano: non si può dedurre dai contratti, e sbaglierebbe in entrambe le direzioni.
 * Un fornitore può essere qualificato per una categoria senza avere contratti in essere — ed è
 * proprio quello interessante, quello da invitare in gara; e può avere un contratto storico per
 * una categoria che non serve più.
 *
 * Si lavora nelle due direzioni perché il censimento si fa in due modi: «di cosa si occupa questo
 * fornitore» quando si apre una scheda, «chi sa fare questa cosa» quando si prepara una gara.
 * Entrambe le PUT **sostituiscono** l'elenco invece di accumulare: un albo si mantiene togliendo
 * le qualifiche decadute quanto aggiungendo le nuove.
 */

import React, { useEffect, useState } from "react";
import { useAuth } from "@shared/context/AuthContext";
import { useToast } from "@shared/components/ui/Toast";
import { alboApi, fornitoriApi, anagraficheApi } from "../services/acquistiApi";
import { Campo, Scelta, Testo, Avviso, Caricamento } from "../components/campi";
import { LIVELLO_RISERVATEZZA, LIVELLO_BADGE } from "../constants/optionSet";
import { etichetta } from "../utils/formato";

const DIREZIONI = [
  { value: "fornitore", label: "Per fornitore — di cosa si occupa" },
  { value: "categoria", label: "Per categoria — chi sa fare questa cosa" },
];

export default function AlboPage() {
  const { showToast } = useToast();
  const { canPerformAction } = useAuth();
  const puoScrivere = canPerformAction("contratti.gestisci");

  const [direzione, setDirezione] = useState("fornitore");
  const [fornitori, setFornitori] = useState([]);
  const [tipologie, setTipologie] = useState([]);
  const [selezionatoId, setSelezionatoId] = useState("");
  const [collegati, setCollegati] = useState(null);
  const [filtro, setFiltro] = useState("");
  const [caricamento, setCaricamento] = useState(false);
  const [inCorso, setInCorso] = useState(false);
  const [errore, setErrore] = useState(null);

  useEffect(() => {
    Promise.allSettled([
      fornitoriApi.list(),
      anagraficheApi.tipologieFornitura(),
    ]).then(([f, t]) => {
      if (f.status === "fulfilled") setFornitori(f.value.value || []);
      if (t.status === "fulfilled") setTipologie(t.value.value || []);
    });
  }, []);

  // Il lato «sorgente» è quello che si sceglie; il lato «bersaglio» è la lista di caselle.
  const perFornitore = direzione === "fornitore";
  const sorgenti = perFornitore ? fornitori : tipologie;
  const bersagli = perFornitore ? tipologie : fornitori;

  useEffect(() => {
    setSelezionatoId("");
    setCollegati(null);
    setFiltro("");
  }, [direzione]);

  useEffect(() => {
    if (!selezionatoId) {
      setCollegati(null);
      return;
    }
    let vivo = true;
    setCaricamento(true);
    setErrore(null);
    const chiamata = perFornitore
      ? alboApi.perFornitore(selezionatoId)
      : alboApi.perCategoria(selezionatoId);
    chiamata
      .then((r) => vivo && setCollegati((r.value || []).map((x) => x.id)))
      .catch((e) => vivo && setErrore(e.data?.message || e.message))
      .finally(() => vivo && setCaricamento(false));
    return () => {
      vivo = false;
    };
  }, [selezionatoId, perFornitore]);

  const toggle = (id) =>
    setCollegati((c) =>
      c.includes(id) ? c.filter((x) => x !== id) : [...c, id],
    );

  const salva = async () => {
    setInCorso(true);
    setErrore(null);
    try {
      const res = perFornitore
        ? await alboApi.salvaPerFornitore(selezionatoId, collegati)
        : await alboApi.salvaPerCategoria(selezionatoId, collegati);
      showToast(
        `Albo aggiornato: +${res.aggiunte} / −${res.rimosse}`,
        "success",
      );
    } catch (e) {
      setErrore(e.data?.message || e.message || "Salvataggio fallito");
    } finally {
      setInCorso(false);
    }
  };

  const visibili = bersagli.filter((b) =>
    filtro ? b.nome?.toLowerCase().includes(filtro.toLowerCase()) : true,
  );

  return (
    <div className="space-y-4 max-w-4xl">
      <h1 className="text-xl font-semibold text-gray-900">Albo fornitori</h1>

      <Avviso tipo="info">
        L'albo dice <strong>chi può fornire cosa</strong>, ed è una
        dichiarazione di idoneità: non si deduce dai contratti in essere. Serve
        a proporre gli invitati a una gara senza ripescarli a memoria.
      </Avviso>

      <div className="card !p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Campo label="Direzione di lavoro">
          <Scelta
            valore={direzione}
            onChange={setDirezione}
            opzioni={DIREZIONI}
            vuoto=""
          />
        </Campo>
        <Campo label={perFornitore ? "Fornitore" : "Categoria"}>
          <Scelta
            valore={selezionatoId}
            onChange={setSelezionatoId}
            opzioni={sorgenti.map((s) => ({ value: s.id, label: s.nome }))}
            vuoto="scegli…"
          />
        </Campo>
      </div>

      {errore && <Avviso tipo="errore">{errore}</Avviso>}

      {!selezionatoId ? (
        <p className="text-sm text-gray-500">
          Scegli {perFornitore ? "un fornitore" : "una categoria"} per vederne
          le qualifiche.
        </p>
      ) : caricamento || collegati === null ? (
        <Caricamento />
      ) : (
        <div className="card space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="font-semibold text-gray-900">
              {perFornitore ? "Categorie fornibili" : "Fornitori qualificati"}
            </h2>
            <span className="text-sm text-gray-500">
              {collegati.length} selezionate su {bersagli.length}
            </span>
            {puoScrivere && (
              <button
                type="button"
                className="btn-primary ml-auto"
                onClick={salva}
                disabled={inCorso}
              >
                {inCorso ? "Salvataggio…" : "Salva albo"}
              </button>
            )}
          </div>

          <Campo label="Filtra l'elenco">
            <Testo valore={filtro} onChange={setFiltro} placeholder="nome" />
          </Campo>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[28rem] overflow-y-auto pr-1">
            {visibili.map((b) => (
              <label
                key={b.id}
                className="flex items-center gap-2 text-sm text-gray-700 py-0.5"
              >
                <input
                  type="checkbox"
                  className="rounded border-gray-300 text-acquisti-600 focus:ring-acquisti-500"
                  checked={collegati.includes(b.id)}
                  onChange={() => toggle(b.id)}
                  disabled={!puoScrivere}
                />
                <span className="min-w-0">{b.nome}</span>
                {/* Chi compone gli inviti deve vedere subito chi è già in trattativa. */}
                {!perFornitore &&
                  b.livello != null &&
                  b.livello !== 100000000 && (
                    <span className={LIVELLO_BADGE[b.livello] || "badge-gray"}>
                      {etichetta(LIVELLO_RISERVATEZZA, b.livello)}
                    </span>
                  )}
              </label>
            ))}
            {visibili.length === 0 && (
              <p className="text-sm text-gray-500">Nessun risultato.</p>
            )}
          </div>

          {!puoScrivere && (
            <Avviso tipo="info">
              Sola lettura: l'azione <code>contratti.gestisci</code> non è
              concessa al tuo ruolo.
            </Avviso>
          )}
        </div>
      )}
    </div>
  );
}
